// Sahneye görsel/video eklerken "ekleniyor" spinner'ı.
//
// Kullanıcı bir görsel/video elemanını sahneye eklediğinde, kutunun gideceği
// yerde dönen bir circle görmek istiyor: tıklama anında çıkmalı ve medya (kare)
// gerçekten yüklenene kadar dönmeli — böylece "bir şey oldu mu?" hissi olmaz.
//
// İmperatif (React state'i taşımaz) — herhangi bir ekleme noktasından tek satırla
// çağrılır: createBox öncesi id + position üret, hemen `spinForSceneAdd(...)` çağır.
//
// Görünüm AssetCell ile aynıdır:
//   _editor/components/carousel/elements_library/ui/_assetCell.scss → cmp-cell__spinner
//   (32px halka, border #e9dbff, top #a963f5, 0.7s linear infinite)

const KEYFRAMES_ID = "scene-add-spinner-keyframes";
const MAX_WAIT = 15000; // medya yüklenmesi için güvenlik üst sınırı
const POLL_MS = 120; // kutu DOM'a düşene kadar arama aralığı
const SPINNER_PX = 32;

// @keyframes'i bir kez <head>'e enjekte et.
function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = "@keyframes scene-add-spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(style);
}

// AssetCell tasarımıyla aynı dönen halka elemanı.
function createSpinnerSpan() {
  const span = document.createElement("span");
  Object.assign(span.style, {
    width: `${SPINNER_PX}px`,
    height: `${SPINNER_PX}px`,
    borderRadius: "50%",
    border: "3px solid #e9dbff",
    borderTopColor: "#a963f5",
    animation: "scene-add-spin 0.7s linear infinite",
    pointerEvents: "none",
  });
  return span;
}

// Spinner'ı ortalayan, tıklamayı engellemeyen overlay kapsayıcı.
function createOverlay() {
  const overlay = document.createElement("div");
  Object.assign(overlay.style, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: "50",
  });
  overlay.appendChild(createSpinnerSpan());
  return overlay;
}

// Kutu içindeki medya (video/img/audio) gerçekten yüklendi mi?
function isMediaReady(media) {
  if (!media) return false;
  if (media.tagName === "VIDEO") return media.readyState >= 2; // HAVE_CURRENT_DATA
  if (media.tagName === "AUDIO") return media.readyState >= 1; // HAVE_METADATA
  return media.complete && media.naturalWidth > 0;
}

/**
 * Tıklama anında çağır. Kendini temizler.
 *
 * @param {object}  opts
 * @param {string}  opts.boxId      createBox'a verilen kutu id'si (DOM: box-<id>)
 * @param {string}  [opts.x]        floating placeholder konumu (ör. "12.34%")
 * @param {string}  [opts.y]
 * @param {string}  [opts.width]    floating placeholder ölçüsü (vars. "20%")
 * @param {string}  [opts.height]
 * @param {boolean} [opts.contained] contained view (mark içeriği) içinde mi
 * @param {string}  [opts.assetType] "image" | "video" | "audio" — ses kutusunda
 *                  görsel medya (img/video) yerine <audio> metadata'sı beklenir.
 */
export function spinForSceneAdd({ boxId, x, y, width, height, contained = false, assetType } = {}) {
  if (typeof document === "undefined" || !boxId) return;

  const airEl = contained
    ? document.getElementById("airlayer_cv") || document.getElementById("airlayer")
    : document.getElementById("airlayer");
  if (!airEl) return; // Visor / canvas yok → no-op

  ensureKeyframes();

  let settled = false;
  let pollTimer = null;
  let safetyTimer = null;
  let overlay = null;

  const cleanup = () => {
    if (settled) return;
    settled = true;
    if (pollTimer) clearTimeout(pollTimer);
    if (safetyTimer) clearTimeout(safetyTimer);
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    overlay = null;
  };

  // Güvenlik: medya hiç yüklenmese bile en geç MAX_WAIT'te kapan.
  safetyTimer = setTimeout(cleanup, MAX_WAIT);

  // 1) ANINDA: kutunun gideceği yerde floating placeholder göster.
  overlay = createOverlay();
  Object.assign(overlay.style, {
    position: "absolute",
    left: x || "0%",
    top: y || "0%",
    width: width || "20%",
    height: height || "20%",
  });
  airEl.appendChild(overlay);

  // 2) Kutu DOM'a düşünce overlay'i onun üstüne taşı, sonra medyayı bekle.
  const tick = () => {
    if (settled) return;
    const boxEl = document.getElementById(`box-${boxId}`);
    if (!boxEl) {
      pollTimer = setTimeout(tick, POLL_MS);
      return;
    }

    // Floating placeholder'dan kutu-içi overlay'e geç (tam kutuya otur).
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    overlay = createOverlay();
    Object.assign(overlay.style, { position: "absolute", inset: "0" });
    // box elemanı position:relative değilse bile inset:0 + absolute, en yakın
    // konumlanmış ata olarak airlayer'a göre hizalanmasın diye kutuyu referans al;
    // EditorBox kutuları position:absolute olduğundan içine eklenen absolute
    // çocuk kutuya göre konumlanır.
    boxEl.appendChild(overlay);

    // Ses kutusunda görsel medya yerine gizli <audio> elemanı beklenir; diğer
    // durumlarda kutu içindeki video/img.
    const mediaSelector = assetType === "audio" ? "audio" : "video, img";
    const media = boxEl.querySelector(mediaSelector);
    if (!media) {
      // Medya elemanı henüz yoksa kısa bir tur daha at (geç render olabilir).
      pollTimer = setTimeout(tick, POLL_MS);
      return;
    }
    if (isMediaReady(media)) return cleanup();

    // Medya yüklenince kapan. Event kaçma ihtimaline karşı düşük frekanslı bir
    // hazır-mı kontrolü de döndür; MAX_WAIT zaten üst sınır.
    const onReady = () => cleanup();
    const evt =
      media.tagName === "VIDEO"
        ? "loadeddata"
        : media.tagName === "AUDIO"
          ? "loadedmetadata"
          : "load";
    media.addEventListener(evt, onReady, { once: true });
    media.addEventListener("error", onReady, { once: true });
    const recheck = () => {
      if (settled) return;
      if (isMediaReady(media)) return cleanup();
      pollTimer = setTimeout(recheck, 400);
    };
    pollTimer = setTimeout(recheck, 400);
  };

  tick();
}

export default spinForSceneAdd;
